import { useEffect, useState, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bold, Italic, Heading1, Heading2, List, ListOrdered, Quote, Code, Undo, Redo, Image as ImageIcon, Link as LinkIcon, Minus } from "lucide-react";

/**
 * Rich text editor (TipTap) with image upload button.
 * Value/onChange sync in HTML string.
 */
export default function RichTextEditor({ value = "", onChange, placeholder = "Tulis konten di sini..." }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, HTMLAttributes: { class: "rounded-xl my-4 max-w-full" } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-[var(--brand-blue)] underline" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-slate max-w-none focus:outline-none min-h-[220px] px-4 py-3",
      },
    },
    onUpdate: ({ editor }) => {
      onChange && onChange(editor.getHTML());
    },
  });

  // Sync when parent value changes externally (edit)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) editor.commands.setContent(value || "", false);
    // eslint-disable-next-line
  }, [value, editor]);

  if (!editor) return <div className="border border-slate-200 rounded-xl h-56 bg-slate-50" />;

  const addLink = () => {
    const url = window.prompt("URL:", editor.getAttributes("link").href || "https://");
    if (url === null) return;
    if (url === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      editor.chain().focus().setImage({ src: data.url, alt: file.name }).run();
      toast.success("Gambar diunggah");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 px-2 py-2 bg-slate-50">
        <ToolBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} icon={Bold} label="Bold" />
        <ToolBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} icon={Italic} label="Italic" />
        <Sep />
        <ToolBtn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} icon={Heading1} label="H1" />
        <ToolBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} icon={Heading2} label="H2" />
        <Sep />
        <ToolBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} icon={List} label="Bullet list" />
        <ToolBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} icon={ListOrdered} label="Ordered list" />
        <ToolBtn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} icon={Quote} label="Quote" />
        <ToolBtn active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} icon={Code} label="Code" />
        <ToolBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} icon={Minus} label="Divider" />
        <Sep />
        <ToolBtn active={editor.isActive("link")} onClick={addLink} icon={LinkIcon} label="Link" />
        <ToolBtn onClick={() => fileRef.current?.click()} icon={ImageIcon} label={uploading ? "Uploading..." : "Insert image"} />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => uploadImage(e.target.files?.[0])}
          data-testid="rte-image-input"
        />
        <div className="ml-auto flex items-center gap-0.5">
          <ToolBtn onClick={() => editor.chain().focus().undo().run()} icon={Undo} label="Undo" />
          <ToolBtn onClick={() => editor.chain().focus().redo().run()} icon={Redo} label="Redo" />
        </div>
      </div>
      <EditorContent editor={editor} data-testid="rte-editor" />
    </div>
  );
}

function ToolBtn({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`h-8 w-8 rounded-lg flex items-center justify-center transition ${active ? "bg-[var(--brand-blue)] text-white" : "text-slate-600 hover:bg-slate-200"}`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
function Sep() { return <div className="h-5 w-px bg-slate-300 mx-1" />; }
